import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import SharedButton from "../../components/Shared/SharedButton";
import TransactionsTable from "./TransactionsTable";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import SearchField from "../../components/Shared/SearchField";
import DatePicker from "../../components/Shared/DatePicker";
import CustomTabs from "../../components/Shared/CustomTabs";
import SharedPageHeader from "../../components/Shared/SharedPageHeader";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import { FiFileText } from "react-icons/fi";
import useFetchData from "../../hooks/useFetchData";
import useDebounce from "../../hooks/useDebounce";
import ReusableModal from "../../components/Shared/ReusableModal";
import validSupergitHIS from "../../api/validSupergitHIS";

export default function TransactionsMain() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const activeTab =
    searchParams.get("tab") || location.state?.activeTab || "journal";
  const currentPage = Number(searchParams.get("page")) || 1;
  const currentPerPage = Number(searchParams.get("per_page")) || 10;

  const [dates, setDates] = useState({
    start_date: "",
    end_date: "",
  });
  const [status, setStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data, loading, executeFetch } = useFetchData();
  const [bulkPostModalVisible, setBulkPostModalVisible] = useState(false);
  const [bulkPostForm, setBulkPostForm] = useState({
    from_date: "",
    to_date: "",
  });
  const [bulkPostStatus, setBulkPostStatus] = useState(null);
  const [bulkPostLoading, setBulkPostLoading] = useState(false);
  const [bulkPostPolling, setBulkPostPolling] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const bulkPostTimeoutRef = useRef(null);
  const bulkPostPollCountRef = useRef(0);

  const tabList = useMemo(
    () => [
      { name: "journal_entries", value: "journal" },
      { name: "outpatient_entries", value: "his" },
      { name: "pharmacy_entries", value: "pharmacy" },
      { name: "supply_chain_entries", value: "supply_chain" },
      { name: "opening_entries", value: "opening_balance" },
    ],
    [],
  );

  const getSourceFilter = useCallback((tab) => {
    switch (tab) {
      case "his":
        return { entry_number_prefix: "INV" };
      case "pharmacy":
        return { entry_number_prefix: "PH" };
      case "supply_chain":
        return { entry_number_prefix: "SC" };
      case "opening_balance":
        return { entry_number_prefix: "OP" };
      default:
        return { entry_number_prefix: "JV" };
    }
  }, []);

  const statusOptions = [
    { label: "Draft", value: "DRAFT" },
    { label: "Posted", value: "POSTED" },
    { label: "Cancelled", value: "CANCELLED" },
  ];

  const handleTabClick = (tabValue) => {
    setSearchParams({ tab: tabValue, page: 1 });
    setDates({
      start_date: "",
      end_date: "",
    });
    setStatus("");
    setSearchQuery("");
  };

  const getPayload = useCallback(
    (customParams = {}) => {
      return {
        page: currentPage,
        per_page: currentPerPage,
        query: searchQuery,
        ...dates,
        ...getSourceFilter(activeTab),
        ...(status ? { status } : {}),
        ...customParams,
      };
    },
    [
      activeTab,
      currentPage,
      currentPerPage,
      searchQuery,
      dates,
      status,
      getSourceFilter,
    ],
  );

  const fetchTransactions = useCallback(
    (customParams = {}) => {
      executeFetch(
        "POST",
        "listTransactions",
        getPayload({
          ...dates,
          start_date: dates?.start_date || "",
          end_date: dates?.end_date || "",
          ...customParams,
        }),
      );
    },
    [dates, executeFetch, getPayload],
  );

  useEffect(() => {
    fetchTransactions({ page: currentPage, per_page: currentPerPage });
  }, [
    activeTab,
    currentPage,
    currentPerPage,
    debouncedSearchQuery,
    fetchTransactions,
  ]);

  useEffect(() => {
    return () => {
      if (bulkPostTimeoutRef.current) {
        clearTimeout(bulkPostTimeoutRef.current);
      }
    };
  }, []);

  const handleTransactionSearchInput = (event) => {
    setSearchQuery(event?.target?.value);
  };

  const handleApplyFilters = () => {
    if (dates.end_date === "" || dates.start_date === "") {
      toast.error("Please Select Dates");
      return;
    }
    executeFetch("POST", "listTransactions", getPayload());
  };

  const handleResetFilters = () => {
    setDates({
      start_date: "",
      end_date: "",
    });
    setSearchQuery("");
    executeFetch("POST", "listTransactions", {
      page: 1,
      per_page: currentPerPage,
      query: "",
      start_date: "",
      end_date: "",
      ...getSourceFilter(activeTab),
    });
    setStatus("");
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    const rows = newRowsPerPage || currentPerPage;
    const targetPage =
      newRowsPerPage && Number(newRowsPerPage) !== Number(currentPerPage)
        ? currentPage
        : newPage;
    setSearchParams({ tab: activeTab, page: targetPage, per_page: rows });
  };

  const clearBulkPostPolling = useCallback(() => {
    if (bulkPostTimeoutRef.current) {
      clearTimeout(bulkPostTimeoutRef.current);
      bulkPostTimeoutRef.current = null;
    }
    bulkPostPollCountRef.current = 0;
    setBulkPostPolling(false);
  }, []);

  const handleBulkPostComplete = useCallback(
    (statusResponse) => {
      clearBulkPostPolling();
      setBulkPostStatus(statusResponse);

      if (statusResponse?.message === "completed") {
        toast.success("Bulk posting completed successfully.");
        fetchTransactions();
        return;
      }

      if (statusResponse?.message === "failed") {
        toast.error(
          "Bulk posting failed. Please review the batch status and retry if needed.",
        );
      }
    },
    [clearBulkPostPolling, fetchTransactions],
  );

  const pollBulkPostStatus = useCallback(
    async (batchNo) => {
      if (!batchNo) return;

      try {
        setBulkPostPolling(true);
        const response = await validSupergitHIS.get(
          `postTransactionBulk/status?batch_no=${encodeURIComponent(batchNo)}`,
        );
        const nextStatus = response?.data || null;
        setBulkPostStatus(nextStatus);

        if (
          nextStatus?.message === "completed" ||
          nextStatus?.message === "failed"
        ) {
          handleBulkPostComplete(nextStatus);
          return;
        }

        bulkPostPollCountRef.current += 1;
        if (bulkPostPollCountRef.current >= 200) {
          clearBulkPostPolling();
          toast.error(
            "Bulk posting is taking longer than expected. Please check again shortly.",
          );
          return;
        }

        bulkPostTimeoutRef.current = setTimeout(() => {
          pollBulkPostStatus(batchNo);
        }, 3000);
      } catch (error) {
        clearBulkPostPolling();
        toast.error(
          error?.response?.data?.error ||
            "Failed to fetch bulk posting status.",
        );
      }
    },
    [clearBulkPostPolling, handleBulkPostComplete],
  );

  const handleBulkPostStart = async () => {
    const { from_date, to_date } = bulkPostForm;

    if (!from_date || !to_date) {
      toast.error("Please select start and end dates");
      return;
    }

    if (from_date && to_date && from_date > to_date) {
      toast.error("from_date cannot be greater than to_date");
      return;
    }

    clearBulkPostPolling();
    setBulkPostLoading(true);

    try {
      const response = await validSupergitHIS.post("postTransactionBulk", {
        from_date: from_date || undefined,
        to_date: to_date || undefined,
      });

      const nextStatus = response?.data || null;
      setBulkPostStatus(nextStatus);

      if (nextStatus?.message === "No draft journals available to post") {
        toast.success(
          "No draft journals were found for the selected date range.",
        );
        return;
      }

      toast.success("Bulk posting started successfully.");

      if (nextStatus?.batch_no) {
        pollBulkPostStatus(nextStatus.batch_no);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to start bulk posting.",
      );
    } finally {
      setBulkPostLoading(false);
    }
  };

  const handleRebuildLedger = async () => {
    setRebuildLoading(true);

    try {
      const response = await validSupergitHIS.post(
        "journal-ledger/rebuild",
        {},
      );
      toast.success(
        response?.data?.message || "Journal ledger rebuilt successfully.",
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          "Accounting update succeeded, but report synchronization failed. Please retry refresh.",
      );
    } finally {
      setRebuildLoading(false);
    }
  };

  const bulkProgress = bulkPostStatus?.total_count
    ? Math.min(
        100,
        Math.round(
          ((Number(bulkPostStatus?.sent_count || 0) +
            Number(bulkPostStatus?.error_count || 0)) /
            Number(bulkPostStatus?.total_count || 1)) *
            100,
        ),
      )
    : 0;

  return (
    <div className="">
      <SharedPageHeader
        title={t(tabList.find((tab) => tab.value === activeTab)?.name)}
        Icon={FiFileText}
        iconSize={20}
        iconClass="!text-primary"
      >
        <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center">
          <div className="flex gap-2">
            <SearchField
              placeholder={t("Search_by_name_or_number")}
              value={searchQuery}
              onChange={handleTransactionSearchInput}
              handleClearClick={() => setSearchQuery("")}
              className="w-full md:!w-[300px]"
            />
            {["journal", "his", "pharmacy", "supply_chain"].includes(
              activeTab,
            ) && (
              <DropdownComponent
                useFormik={false}
                value={status}
                options={statusOptions}
                placeholder={t("status")}
                handleChange={(e) => setStatus(e.value || "")}
                showClear
                className="w-full md:!w-[180px]"
              />
            )}
          </div>
          {activeTab === "journal" && (
            <>
              <SharedButton
                label={t("Bulk Post")}
                icon="pi pi-send"
                onClick={() => setBulkPostModalVisible(true)}
                className="bg-blue"
              />
              <SharedButton
                label={t("Rebuild")}
                icon="pi pi-refresh"
                onClick={handleRebuildLedger}
                disabled={rebuildLoading}
                loading={rebuildLoading}
                className="bg-blue"
              />
              <SharedButton
                label={t("CreateJournalEntries")}
                icon="pi pi-plus"
                onClick={() =>
                  navigate("/manager/transactions/create", {
                    state: { activeTab },
                  })
                }
                className="bg-primary primary-btn"
              />
            </>
          )}
          {activeTab === "opening_balance" && (
            <SharedButton
              label={t("Opening Balance")}
              icon="pi pi-wallet"
              onClick={() =>
                navigate("/manager/opening-balances", {
                  state: { activeTab: "opening_balance" },
                })
              }
              className="bg-primary primary-btn"
            />
          )}
        </div>
      </SharedPageHeader>
      <div className="yee">
        <CustomTabs
          tabs={tabList}
          activeTabIndex={tabList.findIndex((tab) => tab.value === activeTab)}
          setActiveTabIndex={(index) => handleTabClick(tabList[index].value)}
        />
        <div
          className={`md:w-fit flex flex-col sm:flex-row items-center mb-2 gap-2 md:justify-end ${
            isRTL ? "mr-auto" : "ml-auto"
          }`}
        >
          <DatePicker
            useFormik={false}
            placeholder={t("select_start_date")}
            value={dates.start_date ? new Date(dates.start_date) : null}
            onChange={(e) => {
              setDates((prevDates) => ({
                ...prevDates,
                start_date: e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              }));
            }}
            className="w-full md:!w-[250px]"
          />
          <DatePicker
            useFormik={false}
            placeholder={t("select_end_date")}
            value={dates.end_date ? new Date(dates.end_date) : null}
            onChange={(e) => {
              setDates((prevDates) => ({
                ...prevDates,
                end_date: e.value
                  ? new Date(
                      e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .split("T")[0]
                  : "",
              }));
            }}
            className="w-full md:!w-[250px]"
          />
          <div className="">
            <SharedButton
              title={t("apply_filters")}
              icon="pi pi-filter"
              onClick={handleApplyFilters}
            />
          </div>
          <div className="">
            <SharedButton
              buttonType="reset"
              title={t("reset")}
              icon="pi pi-sync"
              onClick={handleResetFilters}
            />
          </div>
        </div>
        <div className="rounded-md">
          <TransactionsTable
            allData={data}
            searchQuery={searchQuery}
            status={status}
            dates={dates}
            loading={loading}
            type={activeTab}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
      <ReusableModal
        position="top"
        title={t("Bulk Journal Posting")}
        isVisible={bulkPostModalVisible}
        onClose={() => setBulkPostModalVisible(false)}
        HeaderIcon={FiFileText}
        className="w-full max-w-[720px] mx-4"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            {t(
              "Post many draft journal entries in the background for the selected date range. Start and end dates are required.",
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DatePicker
              useFormik={false}
              label={t("From Date")}
              required
              value={
                bulkPostForm.from_date ? new Date(bulkPostForm.from_date) : null
              }
              onChange={(e) => {
                setBulkPostForm((prev) => ({
                  ...prev,
                  from_date: e.value
                    ? new Date(
                        e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .split("T")[0]
                    : "",
                }));
              }}
            />
            <DatePicker
              useFormik={false}
              label={t("To Date")}
              required
              value={
                bulkPostForm.to_date ? new Date(bulkPostForm.to_date) : null
              }
              onChange={(e) => {
                setBulkPostForm((prev) => ({
                  ...prev,
                  to_date: e.value
                    ? new Date(
                        e.value.getTime() - e.value.getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .split("T")[0]
                    : "",
                }));
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <SharedButton
              label={t("Reset")}
              buttonType="reset"
              icon="pi pi-sync"
              onClick={() => {
                clearBulkPostPolling();
                setBulkPostForm({ from_date: "", to_date: "" });
                setBulkPostStatus(null);
                setBulkPostModalVisible(false);
              }}
              disabled={bulkPostLoading}
            />
            <SharedButton
              label={t("Post Bulk")}
              icon="pi pi-send"
              onClick={handleBulkPostStart}
              loading={bulkPostLoading}
              disabled={bulkPostPolling}
            />
          </div>

          {bulkPostStatus && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="text-sm font-semibold text-gray-800">
                    Batch {bulkPostStatus?.batch_no || "-"}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">
                    Status: {bulkPostStatus?.message || "unknown"}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {bulkPostPolling
                    ? "Bulk posting is in progress..."
                    : bulkPostStatus?.message === "completed"
                      ? "Bulk posting completed successfully."
                      : bulkPostStatus?.message === "failed"
                        ? "Bulk posting failed. Please review the batch status and retry if needed."
                        : bulkPostStatus?.message || ""}
                </div>
              </div>

              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${bulkProgress}%` }}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div>
                  <div className="text-gray-500">Total</div>
                  <div className="font-semibold">
                    {bulkPostStatus?.total_count ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Sent</div>
                  <div className="font-semibold">
                    {bulkPostStatus?.sent_count ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Errors</div>
                  <div className="font-semibold">
                    {bulkPostStatus?.error_count ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Remaining</div>
                  <div className="font-semibold">
                    {bulkPostStatus?.remaining ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Progress</div>
                  <div className="font-semibold">{bulkProgress}%</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ReusableModal>
    </div>
  );
}
